import { useState, useEffect } from "react";
import { Event } from "../../dtos/event";
import styles from "./guestlist.module.scss";
import baseStyles from "../../styles/App.module.scss";
import classNames from "classnames";
import { InputText } from "primereact/inputtext";
import CustomModal from "../../modal/modal";
import { useFormik } from "formik";
import { respondToEvent } from "../../api";
import { ResponseStatus } from "../../dtos/respondent";
import * as Yup from "yup";
import GuestListDetails from "./guest-list-details";

// Local storage utility functions
const getStorageKey = (eventId: string) => `event_response_${eventId}`;

const saveResponseToStorage = (eventId: string, response: ResponseStatus, fullName: string) => {
  const responseData = {
    status: response,
    fullName: fullName,
    timestamp: Date.now()
  };
  localStorage.setItem(getStorageKey(eventId), JSON.stringify(responseData));
};

const loadResponseFromStorage = (eventId: string): { status: ResponseStatus; fullName: string } | null => {
  try {
    const stored = localStorage.getItem(getStorageKey(eventId));
    if (stored) {
      const responseData = JSON.parse(stored);
      return {
        status: responseData.status,
        fullName: responseData.fullName
      };
    }
  } catch (error) {
    console.error('Error loading response from storage:', error);
  }
  return null;
};

function Guestlist(props: {
  respondents: Event["respondents"];
  eventId: string;
  onResponseSubmitted?: () => void;
}) {
  const [comingBoxisChecked, setComingBoxIsChecked] = useState(false);
  const [maybeBoxisChecked, setMaybeBoxIsChecked] = useState(false);
  const [declinedBoxisChecked, setDeclinedBoxIsChecked] = useState(false);
  const [responseStatus, setResponseStatus] = useState(ResponseStatus.NOT_SET);
  const [modalVisible, setModalVisible] = useState(false);
  const [guestListDetailsVisible, setGuestListDetailsVisible] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState<ResponseStatus>(ResponseStatus.NOT_SET);

  // Load saved response from local storage on component mount
  useEffect(() => {
    const savedResponse = loadResponseFromStorage(props.eventId);
    if (savedResponse) {
      // Set the appropriate checkbox based on saved response
      switch (savedResponse.status) {
        case ResponseStatus.COMING:
          setComingBoxIsChecked(true);
          setMaybeBoxIsChecked(false);
          setDeclinedBoxIsChecked(false);
          setResponseStatus(ResponseStatus.COMING);
          break;
        case ResponseStatus.MAYBE:
          setComingBoxIsChecked(false);
          setMaybeBoxIsChecked(true);
          setDeclinedBoxIsChecked(false);
          setResponseStatus(ResponseStatus.MAYBE);
          break;
        case ResponseStatus.DECLINED:
          setComingBoxIsChecked(false);
          setMaybeBoxIsChecked(false);
          setDeclinedBoxIsChecked(true);
          setResponseStatus(ResponseStatus.DECLINED);
          break;
      }
    }
  }, [props.eventId]);
  const showModal = () => {
    setModalVisible(true);
  };
  const hideModal = () => {
    setModalVisible(false);
  };

  const showGuestListDetails = (status: ResponseStatus) => {
    setSelectedStatus(status);
    setGuestListDetailsVisible(true);
  };

  const hideGuestListDetails = () => {
    setGuestListDetailsVisible(false);
  };
  const handleCheckboxChange = (responseGiven: ResponseStatus) => {
    // Check if there's already a response stored in localStorage
    const savedResponse = loadResponseFromStorage(props.eventId);

    if (savedResponse) {
      // If there's a saved response, directly update the response without showing modal
      const updateResponse = async () => {
        try {
          let respondent = {
            fullName: savedResponse.fullName,
            status: responseGiven,
          };
          await respondToEvent(props.eventId, respondent);

          // Update local storage with new response
          saveResponseToStorage(props.eventId, responseGiven, savedResponse.fullName);

          // Update UI state
          switch (responseGiven) {
            case ResponseStatus.COMING:
              setComingBoxIsChecked(true);
              setMaybeBoxIsChecked(false);
              setDeclinedBoxIsChecked(false);
              setResponseStatus(ResponseStatus.COMING);
              break;
            case ResponseStatus.MAYBE:
              setComingBoxIsChecked(false);
              setMaybeBoxIsChecked(true);
              setDeclinedBoxIsChecked(false);
              setResponseStatus(ResponseStatus.MAYBE);
              break;
            case ResponseStatus.DECLINED:
              setComingBoxIsChecked(false);
              setMaybeBoxIsChecked(false);
              setDeclinedBoxIsChecked(true);
              setResponseStatus(ResponseStatus.DECLINED);
              break;
          }

          // Trigger refresh of event data
          props.onResponseSubmitted?.();
        } catch (error) {
          console.error("Error updating response:", error);
        }
      };

      updateResponse();
    } else {
      // No saved response, show modal for new response
      switch (responseGiven) {
        case ResponseStatus.COMING:
          setComingBoxIsChecked(true);
          setMaybeBoxIsChecked(false);
          setDeclinedBoxIsChecked(false);
          setResponseStatus(ResponseStatus.COMING);
          break;
        case ResponseStatus.MAYBE:
          setComingBoxIsChecked(false);
          setMaybeBoxIsChecked(true);
          setDeclinedBoxIsChecked(false);
          setResponseStatus(ResponseStatus.MAYBE);
          break;
        case ResponseStatus.DECLINED:
          setComingBoxIsChecked(false);
          setMaybeBoxIsChecked(false);
          setDeclinedBoxIsChecked(true);
          setResponseStatus(ResponseStatus.DECLINED);
          break;
      }
      showModal();
    }
  };

  const getStatusTitle = (status: ResponseStatus): string => {

    switch (status) {
      case ResponseStatus.COMING:
        return "Coming";
      case ResponseStatus.MAYBE:
        return "Maybe";
      case ResponseStatus.DECLINED:
        return "Declined";
      case ResponseStatus.NOT_SET:
        return "Not Set";
    }

  }

  return (
    <div>
      <div className={styles.respondentListContainer}>
        <div className={classNames(styles.respondentContainer, styles.coming)}>
          <div
            className={styles.clickableArea}
            onClick={() => showGuestListDetails(ResponseStatus.COMING)}
          >
            <div className={styles.respondentContainerText}>
              Coming (
              {props.respondents && props.respondents.coming
                ? props.respondents.coming.length
                : 0}
              )
            </div>
          </div>
          <div className={classNames(styles.tickboxContainer, styles.coming)}>
            <input
              type="checkbox"
              checked={comingBoxisChecked}
              onChange={() => handleCheckboxChange(ResponseStatus.COMING)}
              className={styles._checkbox}
              id="checkbox1"
            />
            <label className={styles.comingTickbox} htmlFor="checkbox1">
              <div className={styles.tickMark}></div>
            </label>
          </div>
        </div>
        <div className={classNames(styles.respondentContainer, styles.maybe)}>
          <div
            className={styles.clickableArea}
            onClick={() => showGuestListDetails(ResponseStatus.MAYBE)}
          >
            <div className={styles.respondentContainerText}>
              Maybe (
              {props.respondents && props.respondents.maybe
                ? props.respondents.maybe.length
                : 0}
              )
            </div>
          </div>
          <div className={classNames(styles.tickboxContainer, styles.maybe)}>
            <input
              type="checkbox"
              checked={maybeBoxisChecked}
              onChange={() => handleCheckboxChange(ResponseStatus.MAYBE)}
              className={styles._checkbox}
              id="checkbox2"
            />
            <label className={styles.maybeTickbox} htmlFor="checkbox2">
              <div className={styles.tickMark}></div>
            </label>
          </div>
        </div>
        <div
          className={classNames(styles.respondentContainer, styles.declined)}
        >
          <div
            className={styles.clickableArea}
            onClick={() => showGuestListDetails(ResponseStatus.DECLINED)}
          >
            <div className={styles.respondentContainerText}>
              Declined (
              {props.respondents && props.respondents.declined
                ? props.respondents.declined.length
                : 0}
              )
            </div>

          </div>
          <div className={classNames(styles.tickboxContainer, styles.declined)}>
            <input
              type="checkbox"
              checked={declinedBoxisChecked}
              onChange={() => handleCheckboxChange(ResponseStatus.DECLINED)}
              className={styles._checkbox}
              id="checkbox3"
            />
            <label className={styles.declinedTickbox} htmlFor="checkbox3">
              <div className={styles.tickMark}></div>
            </label>
          </div>
        </div>
      </div>
      <CustomModal
        visible={modalVisible}
        onHide={hideModal}
        header="Respond With Your Name"
      >
        <ResponseForm
          onSubmit={hideModal}
          responseStatus={responseStatus}
          eventId={props.eventId}
          onResponseSubmitted={props.onResponseSubmitted}
        />
      </CustomModal>

      <CustomModal
        visible={guestListDetailsVisible}
        onHide={hideGuestListDetails}
        header={<h3>{getStatusTitle(selectedStatus)}</h3>}
      >
        <GuestListDetails
          respondents={props.respondents || { coming: [], maybe: [], declined: [] }}
          status={selectedStatus}
        />
      </CustomModal>
    </div>
  );
}

const validationSchema = Yup.object().shape({
  fullName: Yup.string()
    .required("Name is required")
    .min(1, "Name must be at least 1 character")
    .max(50, "Name cannot be longer than 50 characters"),
});

const ResponseForm = (props: {
  onSubmit: Function;
  responseStatus: ResponseStatus;
  eventId: string;
  onResponseSubmitted?: () => void;
}) => {
  // Load saved name from local storage for pre-population
  const savedResponse = loadResponseFromStorage(props.eventId);
  const initialFormValues = {
    fullName: savedResponse?.fullName || "",
  };

  const formik = useFormik({
    initialValues: initialFormValues,
    validationSchema,
    validateOnMount: true,
    validateOnChange: true,
    validateOnBlur: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        let respondent = {
          fullName: values.fullName,
          status: props.responseStatus,
        };
        await respondToEvent(props.eventId, respondent);

        // Save response to local storage
        saveResponseToStorage(props.eventId, props.responseStatus, values.fullName);

        props.onResponseSubmitted?.();
        props.onSubmit();
      } catch (error) {
        console.error("Submission error:", error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  return (
    <div>
      <form onSubmit={formik.handleSubmit}>
        <div className="p-mt-8">
          <InputText
            type="text"
            id="fullName"
            name="fullName"
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
            value={formik.values.fullName}
            className={
              formik.errors.fullName && formik.touched.fullName
                ? "p-invalid"
                : ""
            }
            placeholder="Full Name"
          />
          {formik.errors.fullName && formik.touched.fullName && (
            <div className="error-message">{formik.errors.fullName}</div>
          )}
        </div>

        <button
          type="submit"
          className={classNames(
            baseStyles.btn,
            baseStyles.btnPrimary,
            baseStyles.btnMd,
            baseStyles.centerVer,
            "mt-3"
          )}
          disabled={!formik.isValid}
        >
          Respond
        </button>
      </form>
    </div>
  );
};

export default Guestlist;
